import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { rateFreshness, askVendorForRates } from "../../services/catalogueService";
import { errorToast, successToast } from "../Toasts/Toasts";

// How old the prices we buy at are, and what to do about it.
//
// Two ways to refresh one, and they are not the same thing. Asking the vendor
// costs a message and takes minutes, but it is their number. Sending our own
// person costs a visit, but it is what we saw. The checklist is for the
// second: print it, take it, write what it is today.
//
// Sorted so a stale cost under a live customer price comes first - that one
// is not untidy, it is a wrong price a society is being charged.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const ageClass = (d) => (d > 365 ? "text-danger fw-semibold" : d > 180 ? "text-warning" : "text-muted");

const RateFreshness = () => {
  const [data, setData] = useState(null);
  const [staleDays, setStaleDays] = useState(90);
  const [onlyListed, setOnlyListed] = useState(false);
  const [vendor, setVendor] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);

  const load = useCallback(async () => {
    try { setData(await rateFreshness({ staleDays, onlyListed })); } catch (e) { errorToast("Could not load"); }
  }, [staleDays, onlyListed]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await askVendorForRates(ask.vendorId, { max: 8 });
      if (r?.success) { successToast(`Asked ${r.data.vendor} about ${r.data.asked.length} item(s)`); setAsk(null); }
      else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  if (!data) return null;
  const rows = vendor ? data.rows.filter((r) => r.vendor === vendor) : data.rows;

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0">How fresh are our costs</h6>
            <div className="small text-muted">
              {data.stale} of {data.total} vendor prices are older than {data.staleDays} days
              {data.liveAtRisk > 0 && <span className="text-danger"> · {data.liveAtRisk} sit under a live customer price</span>}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Form.Check type="switch" id="listed" label="Only what we sell" checked={onlyListed} onChange={(e) => setOnlyListed(e.target.checked)} className="small" />
            <Form.Select size="sm" value={staleDays} onChange={(e) => setStaleDays(Number(e.target.value))} style={{ width: 150 }}>
              <option value={30}>older than 30 days</option>
              <option value={90}>older than 90 days</option>
              <option value={180}>older than 6 months</option>
              <option value={365}>older than a year</option>
            </Form.Select>
          </div>
        </div>

        <div className="card mt-2"><div className="card-body py-2">
          <div className="small fw-semibold mb-2">By vendor — ask the ones with the most, or send someone</div>
          <div className="d-flex flex-wrap gap-2">
            {data.vendors.map((v) => (
              <span key={v.vendor} className={`badge border d-inline-flex align-items-center gap-2 py-2 ${vendor === v.vendor ? "bg-dark text-white" : "bg-light text-dark"}`}>
                <button className="btn btn-link p-0 text-reset text-decoration-none" onClick={() => setVendor(vendor === v.vendor ? "" : v.vendor)}>
                  {v.vendor} <b>{v.items}</b>
                  {v.live > 0 && <span className="text-danger"> · {v.live} live</span>}
                </button>
                {v.vendorId && <Button size="sm" variant="link" className="p-0 small" onClick={() => setAsk(v)}>ask</Button>}
              </span>
            ))}
          </div>
        </div></div>

        <div className="card mt-2"><div className="card-body p-0">
          <div className="table-responsive table-scroll"><table className="table table-sm mb-0 align-middle">
            <thead><tr><th>Item</th><th>Vendor</th><th className="text-end">We pay</th><th className="text-end">We sell</th><th>Last quoted</th><th className="text-end">Age</th></tr></thead>
            <tbody>
              {rows.slice(0, 300).map((r) => (
                <tr key={`${r.vendorId}-${r.variantId}`} className={r.live ? "table-warning" : ""}>
                  <td className="small">{r.variantName}{r.live && <span className="badge bg-danger ms-1">live price</span>}</td>
                  <td className="small text-muted">{r.vendor}</td>
                  <td className="text-end">{inr(r.rate)}</td>
                  <td className="text-end">{r.sellsAt ? inr(r.sellsAt) : <span className="text-muted">not sold</span>}</td>
                  <td className="small text-muted">{r.date}</td>
                  <td className={`text-end small ${ageClass(r.age)}`}>{r.age > 365 ? `${Math.round(r.age / 365)} yr` : `${r.age} d`}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="text-muted p-3">Nothing older than that. </td></tr>}
            </tbody>
          </table></div>
        </div></div>
        {rows.length > 300 && <div className="small text-muted mt-2 px-2">Showing the 300 most urgent of {rows.length}. Narrow by vendor to see the rest.</div>}
        <div className="small text-muted mt-2 px-2">
          A rate a vendor gives on WhatsApp is logged the moment they reply. A rate our own person brings back is entered on the product page, so its source says who saw it — see <Link to="/catalogue/products">the catalogue</Link>.
        </div>
      </div>

      <Modal show={!!ask} onHide={() => setAsk(null)}>
        <Modal.Header closeButton><Modal.Title>Ask {ask?.vendor} for today's rates</Modal.Title></Modal.Header>
        {ask && <Modal.Body>
          <div>They have <b>{ask.items}</b> prices older than {data.staleDays} days, the oldest {ask.oldest} days old.</div>
          <div className="small text-muted mt-2">
            One WhatsApp message, in your own wording, listing at most eight items — a vendor sent forty lines answers none of them.
            Their reply is read and logged the way any vendor reply is.
          </div>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy} onClick={send}>Send the ask</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default RateFreshness;
